import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useHistory, Link, useLocation, Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Alert } from '@mantine/core'
import { AppButton, AppText } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { FirearmModelFormValues } from '../../app/models/firearmModel'
import agent from '../../app/api/agent'
import { TrustBadge } from '../../app/common/ui'

const STEP_LABELS = ['Platform', 'Make', 'Caliber', 'Details', 'Review']
const TOTAL_STEPS = 5

const PLATFORM_OPTIONS: { value: string; abbr: string }[] = [
  { value: 'AR-15', abbr: 'AR' },
  { value: 'AK-Pattern', abbr: 'AK' },
  { value: 'Pistol', abbr: 'PI' },
  { value: 'Glock', abbr: 'GL' },
  { value: '1911', abbr: '45' },
  { value: 'Bolt Action', abbr: 'BA' },
  { value: 'Lever Action', abbr: 'LA' },
  { value: 'Shotgun', abbr: 'SG' },
  { value: 'Revolver', abbr: 'RV' },
  { value: 'Other', abbr: '?' }
]

/** Brand accent colors for manufacturer cards (logo vibes). */
const MANUFACTURERS: { name: string; country: string; brandColor: string }[] = [
  { name: 'Glock', country: 'AT', brandColor: '#2d2d2d' },
  { name: 'BCM', country: 'USA', brandColor: '#c4a35a' },
  { name: 'Daniel Defense', country: 'USA', brandColor: '#FFC72D' },
  { name: 'Ruger', country: 'USA', brandColor: '#8b4513' },
  { name: 'Smith & Wesson', country: 'USA', brandColor: '#013C79' },
  { name: 'Sig Sauer', country: 'USA/DE', brandColor: '#9e0b0b' },
  { name: 'Springfield', country: 'USA', brandColor: '#1e3a5f' },
  { name: 'Colt', country: 'USA', brandColor: '#002878' },
  { name: 'Mossberg', country: 'USA', brandColor: '#2c5234' },
  { name: 'Savage', country: 'USA', brandColor: '#3d5c3d' },
  { name: 'Winchester', country: 'USA', brandColor: '#8b0000' },
  { name: 'Remington', country: 'USA', brandColor: '#2f4f4f' },
  { name: 'FN', country: 'BE', brandColor: '#043651' },
  { name: 'HK', country: 'DE', brandColor: '#c8102e' },
  { name: 'Beretta', country: 'IT', brandColor: '#003366' },
  { name: 'CZ', country: 'CZ', brandColor: '#8b0000' }
]

const CALIBERS: { value: string; fullName: string }[] = [
  { value: '9mm', fullName: '9×19 Para' },
  { value: '5.56', fullName: '5.56×45 NATO' },
  { value: '.308 Win', fullName: '7.62×51 NATO' },
  { value: '7.62×39', fullName: 'AK standard' },
  { value: '.45 ACP', fullName: '45 Auto' },
  { value: '.22 LR', fullName: 'Rimfire' },
  { value: '6.5 CM', fullName: 'Creedmoor' },
  { value: '.300 BLK', fullName: 'Blackout' },
  { value: '.40 S&W', fullName: 'Smith & Wesson' },
  { value: '.380 ACP', fullName: 'Auto' },
  { value: '12 Gauge', fullName: 'Shotgun' },
  { value: '20 Gauge', fullName: 'Shotgun' },
  { value: '.357 Mag', fullName: 'Magnum' },
  { value: '.44 Mag', fullName: 'Big iron' }
]

/** Hex to rgba for opacity. */
function hexToRgba (hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

/** True if color is so dark it should use default (non-inverted) card styling. */
function isDarkBrandColor (hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const lum = 0.299 * r + 0.587 * g + 0.114 * b
  return lum < 0.15
}

const cardBase = {
  background: 'var(--gs-surface)',
  border: '2px solid var(--gs-border)',
  borderRadius: 8,
  padding: '16px 12px',
  textAlign: 'center' as const,
  cursor: 'pointer',
  transition: 'border-color 0.15s, background 0.15s',
  '&:hover': { borderColor: '#E85D04' }
}
const cardSelected = {
  borderColor: '#E85D04',
  background: 'rgba(232,93,4,0.08)'
}

function getOrderedCalibers (platform: string | null, make: string | null): typeof CALIBERS {
  const prioritize = (...firsts: string[]) => {
    const rest = CALIBERS.filter((c) => !firsts.includes(c.value))
    return [...firsts.map((v) => CALIBERS.find((c) => c.value === v)!).filter(Boolean), ...rest]
  }
  if (make === 'Mossberg') return prioritize('12 Gauge', '20 Gauge')
  if (make !== null && ['Glock', 'Smith & Wesson', 'Beretta'].includes(make)) return prioritize('9mm', '.40 S&W')
  if (platform === 'AR-15') return prioritize('5.56')
  if (platform === 'Pistol') return prioritize('9mm', '.45 ACP', '.40 S&W')
  return prioritize('5.56')
}

function getInitial (name: string): string {
  const n = name.trim()
  if (!n) return '?'
  const first = n[0]
  const space = n.indexOf(' ')
  if (space > 0) return (first + n[space + 1]).toUpperCase()
  return first.toUpperCase()
}

export default observer(function RegistryForm () {
  const { firearmModelStore } = useStore()
  const { createModel, loading } = firearmModelStore
  const history = useHistory()
  const location = useLocation()

  const [isReturnFlow] = useState(() => {
    const params = new URLSearchParams(location.search)
    const returnToPost = params.get('returnTo') === 'createPost'
    const linkedAfterCreate = params.get('linkedAfterCreate') === 'true'
    return returnToPost && linkedAfterCreate
  })
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const [step, setStep] = useState(1)
  const [platform, setPlatform] = useState<string | null>(null)
  const [make, setMake] = useState<string | null>(null)
  const [makeOther, setMakeOther] = useState('')
  const [makeIsOther, setMakeIsOther] = useState(false)
  const [caliber, setCaliber] = useState<string | null>(null)
  const [caliberOther, setCaliberOther] = useState('')
  const [caliberIsOther, setCaliberIsOther] = useState(false)
  const [modelName, setModelName] = useState('')
  const [yearIntroduced, setYearIntroduced] = useState<string>('')
  const [countryOfOrigin, setCountryOfOrigin] = useState('')
  const [description, setDescription] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [aiFillQuery, setAiFillQuery] = useState('')
  const [aiFillLoading, setAiFillLoading] = useState(false)
  const [aiFillError, setAiFillError] = useState<string | null>(null)
  const [aiFilled, setAiFilled] = useState(false)

  // Other manufacturer: use customMake state value, not selectedMake card label.
  const resolvedMake =
    make === 'Other' ? (makeOther.trim() || null) : make !== null ? make : null
  const resolvedCaliber = caliber !== null ? caliber : (caliberOther.trim() || null)

  const canProceedStep1 = platform != null
  const canProceedStep2 = resolvedMake != null && resolvedMake.length > 0
  const canProceedStep3 = resolvedCaliber != null && resolvedCaliber.length > 0
  const canProceedStep4 = modelName.trim().length > 0

  const canProceed =
    (step === 1 && canProceedStep1) ||
    (step === 2 && canProceedStep2) ||
    (step === 3 && canProceedStep3) ||
    (step === 4 && canProceedStep4) ||
    step === 5

  const handleAiFill = async () => {
    const query = aiFillQuery.trim()
    if (!query) return
    setAiFillLoading(true)
    setAiFillError(null)
    setAiFilled(false)
    try {
      const result = await agent.AiAssist.registryFill(query)

      // Map platform to a known option
      const knownPlatform = result.platform
        ? (PLATFORM_OPTIONS.find(o => o.value.toLowerCase() === result.platform!.toLowerCase())?.value ?? 'Other')
        : null
      if (knownPlatform) setPlatform(knownPlatform)

      // Map make to a known manufacturer or "Other"
      if (result.make) {
        const knownMake = MANUFACTURERS.find(m => m.name.toLowerCase() === result.make!.toLowerCase())
        if (knownMake) {
          setMake(knownMake.name)
          setMakeOther('')
          setMakeIsOther(false)
        } else {
          setMake('Other')
          setMakeOther(result.make)
          setMakeIsOther(true)
        }
      }

      // Map caliber to a known option or "Other"
      if (result.caliber) {
        const knownCaliber = CALIBERS.find(c => c.value.toLowerCase() === result.caliber!.toLowerCase())
        if (knownCaliber) {
          setCaliber(knownCaliber.value)
          setCaliberOther('')
          setCaliberIsOther(false)
        } else {
          setCaliber(null)
          setCaliberOther(result.caliber)
          setCaliberIsOther(true)
        }
      }

      if (result.model) setModelName(result.model)
      if (result.yearIntroduced) setYearIntroduced(String(result.yearIntroduced))
      if (result.description) setDescription(result.description)

      setAiFilled(true)
      setStep(5)
    } catch (err: unknown) {
      const is503 = (err as any)?.response?.status === 503
      setAiFillError(is503 ? 'AI service is currently offline.' : 'AI fill failed. Please try again or fill the form manually.')
    } finally {
      setAiFillLoading(false)
    }
  }

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(step + 1)
  }
  const handleBack = () => {
    if (step > 1) setStep(step - 1)
  }

  const handleSubmit = async () => {
    setSubmitError(null)
    const payload: FirearmModelFormValues = {
      make: resolvedMake!,
      model: modelName.trim(),
      platform: platform || undefined,
      caliber: resolvedCaliber || undefined,
      yearIntroduced: yearIntroduced.trim() ? parseInt(yearIntroduced, 10) || undefined : undefined,
      description: description.trim() || undefined
    }
    try {
      const newId = await createModel(payload)
      if (newId) {
        toast.success('Submitted! Your model is in the review queue.', { autoClose: 6000 })
        setSubmitSuccess(true)
        if (isReturnFlow) {
          history.push(
            `/createPost?resumeDraft=true&linkedFirearmId=${encodeURIComponent(newId)}`
          )
          return
        }
        history.push(`/registry/${newId}`)
      }
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to submit.'
      setSubmitError(msg)
    }
  }

  return (
    <Box
      sx={{
        maxWidth: 640,
        margin: '52px auto 0',
        padding: 24,
        background: 'var(--gs-bg)'
      }}
    >
      <Prompt
        when={isReturnFlow && !submitSuccess}
        message="Your review draft is saved — leaving won't lose it."
      />
      {isReturnFlow && (
        <Alert variant="light" color="blue" mb="md">
          You&apos;re registering a firearm to link to your post. After submitting, you&apos;ll be returned to
          your draft automatically.
        </Alert>
      )}
      {/* Header */}
      <AppText
        component="h1"
        sx={{
          fontFamily: 'Oswald, sans-serif',
          fontWeight: 700,
          fontSize: 20,
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          color: 'var(--gs-text)',
          margin: '0 0 6px 0'
        }}
      >
        SUBMIT A FIREARM MODEL
      </AppText>
      <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)', marginBottom: 10 }}>
        New entries go to the moderator queue before appearing in the Registry.
      </AppText>
      <Link
        to={{
          pathname: '/registry/full-form',
          search: location.search,
          state: {
            draft: {
              platform: platform ?? '',
              make: resolvedMake ?? '',
              caliber: resolvedCaliber ?? '',
              model: modelName,
              yearIntroduced: yearIntroduced.trim() ? parseInt(yearIntroduced, 10) || undefined : undefined,
              description: description.trim() || ''
            }
          }
        }}
        style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 12,
          color: '#58A6FF',
          textDecoration: 'underline'
        }}
      >
        Skip wizard — view full form instead →
      </Link>

      {/* AI Fill */}
      <Box
        sx={{
          marginTop: 20,
          padding: '14px 16px',
          background: 'rgba(88,166,255,0.05)',
          border: '1px solid rgba(88,166,255,0.2)',
          borderRadius: 8
        }}
      >
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            color: '#58A6FF',
            marginBottom: 8
          }}
        >
          Let AI fill the form
        </AppText>
        <Box sx={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder='e.g. "HK416 D14.5RS" or "Glock 19 Gen 5"'
            value={aiFillQuery}
            onChange={(e) => setAiFillQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAiFill() }}
            style={{
              flex: 1,
              background: 'var(--gs-surface2)',
              border: '1.5px solid rgba(88,166,255,0.3)',
              color: 'var(--gs-text)',
              padding: '8px 12px',
              borderRadius: 5,
              fontSize: 13,
              fontFamily: 'DM Sans, sans-serif'
            }}
          />
          <AppButton
            onClick={handleAiFill}
            loading={aiFillLoading}
            disabled={!aiFillQuery.trim()}
            sx={{
              background: 'rgba(88,166,255,0.15)',
              border: '1px solid rgba(88,166,255,0.3)',
              color: '#58A6FF',
              '&:hover': { background: 'rgba(88,166,255,0.25)' },
              flexShrink: 0
            }}
          >
            Fill
          </AppButton>
        </Box>
        {aiFillError && (
          <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: '#f85149', marginTop: 6 }}>
            {aiFillError}
          </AppText>
        )}
        {aiFilled && (
          <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
            <TrustBadge variant="ai-seeded" />
            <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>
              Review each field before submitting — AI suggestions may contain errors.
            </AppText>
          </Box>
        )}
        <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)', marginTop: 6 }}>
          Or use the steps below to fill it manually.
        </AppText>
      </Box>

      {/* Progress */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', marginTop: 28, marginBottom: 32 }}>
        {STEP_LABELS.map((label, i) => {
          const idx = i + 1
          const active = step === idx
          const completed = step > idx
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', flex: i === STEP_LABELS.length - 1 ? 0 : 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: active ? '#E85D04' : completed ? '#3FB950' : '#21262D',
                    border: active || completed ? 'none' : '2px solid #484F58',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: completed ? '#fff' : active ? '#fff' : 'var(--gs-muted)',
                    fontSize: 12,
                    fontWeight: 700
                  }}
                >
                  {completed ? '✓' : idx}
                </Box>
                <AppText
                  sx={{
                    marginTop: 6,
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 11,
                    color: active ? 'var(--gs-text)' : 'var(--gs-muted)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {label}
                </AppText>
              </Box>
              {i < STEP_LABELS.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    background: completed ? '#3FB950' : 'var(--gs-border)',
                    marginLeft: 8,
                    marginRight: 8,
                    marginTop: 14,
                    minWidth: 12
                  }}
                />
              )}
            </Box>
          )
        })}
      </Box>

      {/* Step 1 — Platform */}
      {step === 1 && (
        <>
          <AppText
            component="h2"
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 500,
              fontSize: 16,
              textTransform: 'uppercase',
              color: 'var(--gs-text)',
              marginBottom: 16
            }}
          >
            WHAT PLATFORM IS IT?
          </AppText>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12
            }}
          >
            {PLATFORM_OPTIONS.map((opt) => (
              <Box
                key={opt.value}
                component="button"
                type="button"
                onClick={() => setPlatform(opt.value)}
                sx={{
                  ...cardBase,
                  ...(platform === opt.value ? cardSelected : {})
                }}
              >
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 14, color: 'var(--gs-text)' }}>
                  {opt.value}
                </AppText>
                <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--gs-muted)', marginTop: 4, letterSpacing: 1 }}>{opt.abbr}</AppText>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Step 2 — Make */}
      {step === 2 && (
        <>
          <AppText
            component="h2"
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 500,
              fontSize: 16,
              textTransform: 'uppercase',
              color: 'var(--gs-text)',
              marginBottom: 16
            }}
          >
            WHO MAKES IT?
          </AppText>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {MANUFACTURERS.map((m) => {
              const selected = make === m.name
              const c = m.brandColor
              const useInversion = !isDarkBrandColor(c)
              const cardStyles = useInversion
                ? selected
                  ? {
                      background: '#21262D',
                      border: '2px solid #484F58',
                      '& .card-initial': {
                        background: 'var(--gs-surface2)',
                        border: '1.5px solid #484F58',
                        color: '#8B949E'
                      },
                      '& .card-name': { color: '#8B949E' },
                      '& .card-country': { color: '#8B949E' }
                    }
                  : {
                      background: hexToRgba(c, 0.15),
                      border: `1px solid ${hexToRgba(c, 0.4)}`,
                      '&:hover': { borderColor: hexToRgba(c, 0.6), background: hexToRgba(c, 0.22) },
                      '& .card-initial': {
                        background: hexToRgba(c, 0.25),
                        border: `1.5px solid ${c}`,
                        color: c
                      },
                      '& .card-name': { color: c },
                      '& .card-country': { color: hexToRgba(c, 0.8) }
                    }
                : {
                    '&:hover': { borderColor: c, background: `${c}12` },
                    ...(selected
                      ? { borderColor: c, background: `${c}18` }
                      : {}),
                    '& .card-initial': selected
                      ? { background: `${c}30`, border: `1.5px solid ${c}`, color: c }
                      : { background: 'var(--gs-surface2)', border: '1.5px solid var(--gs-border-bright)', color: 'var(--gs-text)' },
                    '& .card-name': { color: 'var(--gs-text)' },
                    '& .card-country': { color: 'var(--gs-muted)' }
                  }
              return (
                <Box
                  key={m.name}
                  component="button"
                  type="button"
                  onClick={() => {
                    setMake(m.name)
                    setMakeOther('')
                    setMakeIsOther(false)
                  }}
                  sx={{
                    ...cardBase,
                    background: selected ? '#21262D' : `${c}26`,
                    border: selected ? '2px solid #484F58' : `1.5px solid ${c}66`,
                    '&:hover': { borderColor: c, background: selected ? '#21262D' : `${c}33` }
                  }}
                >
                  <Box
                    className="card-initial"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: selected ? 'var(--gs-surface2)' : `${c}30`,
                      border: `1.5px solid ${selected ? 'var(--gs-border-bright)' : c}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 8px',
                      fontFamily: 'Oswald, sans-serif',
                      fontWeight: 700,
                      fontSize: 14,
                      color: selected ? '#8B949E' : c
                    }}
                  >
                    {getInitial(m.name)}
                  </Box>
                  <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13, color: selected ? '#8B949E' : c }}>
                    {m.name}
                  </AppText>
                  <AppText className="card-country" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10 }}>
                    {m.country}
                  </AppText>
                </Box>
              )
            })}
            <Box
              component="button"
              type="button"
              onClick={() => {
                setMake('Other')
                setMakeOther('')
                setMakeIsOther(true)
              }}
              sx={{
                ...cardBase,
                borderStyle: 'dashed',
                ...(makeIsOther ? cardSelected : {})
              }}
            >
              <AppText sx={{ fontSize: 20, marginBottom: 4 }}>+</AppText>
              <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
                Other — Type below
              </AppText>
            </Box>
          </Box>
          {makeIsOther && (
            <Box sx={{ marginTop: 16 }}>
              <input
                type="text"
                placeholder="Or type manufacturer name..."
                value={makeOther}
                onChange={(e) => {
                  setMakeOther(e.target.value)
                  setMake('Other')
                }}
                autoFocus
                style={{
                  width: '100%',
                  background: 'var(--gs-surface2)',
                  border: '1.5px solid var(--gs-border-bright)',
                  color: 'var(--gs-text)',
                  padding: '10px 14px',
                  borderRadius: 5,
                  fontSize: 13,
                  fontFamily: 'DM Sans, sans-serif',
                  boxSizing: 'border-box'
                }}
              />
              {makeOther.trim() === '' && (
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)', marginTop: 4 }}>
                  Please enter a manufacturer name
                </AppText>
              )}
            </Box>
          )}
        </>
      )}

      {/* Step 3 — Caliber */}
      {step === 3 && (
        <>
          <AppText
            component="h2"
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 500,
              fontSize: 16,
              textTransform: 'uppercase',
              color: 'var(--gs-text)',
              marginBottom: 16
            }}
          >
            WHAT DOES IT EAT?
          </AppText>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {getOrderedCalibers(platform, resolvedMake).map((c) => (
              <Box
                key={c.value}
                component="button"
                type="button"
                onClick={() => {
                  setCaliber(c.value)
                  setCaliberOther('')
                  setCaliberIsOther(false)
                }}
                sx={{
                  ...cardBase,
                  ...(caliber === c.value && !caliberOther ? cardSelected : {})
                }}
              >
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 700,
                    fontSize: 16,
                    color: '#E85D04'
                  }}
                >
                  {c.value}
                </AppText>
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)', marginTop: 4 }}>
                  {c.fullName}
                </AppText>
              </Box>
            ))}
            <Box
              component="button"
              type="button"
              onClick={() => {
                setCaliber(null)
                setCaliberOther('')
                setCaliberIsOther(true)
              }}
              sx={{
                ...cardBase,
                borderStyle: 'dashed',
                ...(caliberIsOther ? cardSelected : {})
              }}
            >
              <AppText sx={{ fontSize: 20, marginBottom: 4 }}>+</AppText>
              <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
                Other — type below
              </AppText>
            </Box>
          </Box>
          {caliberIsOther && (
            <Box sx={{ marginTop: 16 }}>
              <input
                type="text"
                placeholder="Or type caliber..."
                value={caliberOther}
                onChange={(e) => {
                  setCaliberOther(e.target.value)
                  if (e.target.value.trim()) setCaliber(null)
                }}
                autoFocus
                style={{
                  width: '100%',
                  background: 'var(--gs-surface2)',
                  border: '1.5px solid var(--gs-border-bright)',
                  color: 'var(--gs-text)',
                  padding: '10px 14px',
                  borderRadius: 5,
                  fontSize: 13,
                  fontFamily: 'DM Sans, sans-serif',
                  boxSizing: 'border-box'
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Step 4 — Details */}
      {step === 4 && (
        <>
          <AppText
            component="h2"
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 500,
              fontSize: 16,
              textTransform: 'uppercase',
              color: 'var(--gs-text)',
              marginBottom: 16
            }}
          >
            ALMOST DONE — JUST THE DETAILS
          </AppText>
          <Box sx={{ marginBottom: 14 }}>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: 'var(--gs-muted)',
                marginBottom: 6
              }}
            >
              Model Name *
            </AppText>
            <input
              type="text"
              placeholder="e.g. RECCE-16 MCMR, Glock 19 Gen 5, Model 700"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              style={{
                width: '100%',
                background: 'var(--gs-surface2)',
                border: '1.5px solid var(--gs-border-bright)',
                color: 'var(--gs-text)',
                padding: '10px 14px',
                borderRadius: 5,
                fontSize: 13,
                fontFamily: 'DM Sans, sans-serif',
                boxSizing: 'border-box'
              }}
            />
          </Box>
          <Box sx={{ marginBottom: 14 }}>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: 'var(--gs-muted)',
                marginBottom: 6
              }}
            >
              Year Introduced
            </AppText>
            <input
              type="number"
              placeholder="e.g. 2018"
              value={yearIntroduced}
              onChange={(e) => setYearIntroduced(e.target.value)}
              style={{
                width: '100%',
                background: 'var(--gs-surface2)',
                border: '1.5px solid var(--gs-border-bright)',
                color: 'var(--gs-text)',
                padding: '10px 14px',
                borderRadius: 5,
                fontSize: 13,
                fontFamily: 'DM Sans, sans-serif',
                boxSizing: 'border-box'
              }}
            />
          </Box>
          <Box sx={{ marginBottom: 14 }}>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: 'var(--gs-muted)',
                marginBottom: 6
              }}
            >
              Country of Origin
            </AppText>
            <input
              type="text"
              placeholder="e.g. USA"
              value={countryOfOrigin}
              onChange={(e) => setCountryOfOrigin(e.target.value)}
              style={{
                width: '100%',
                background: 'var(--gs-surface2)',
                border: '1.5px solid var(--gs-border-bright)',
                color: 'var(--gs-text)',
                padding: '10px 14px',
                borderRadius: 5,
                fontSize: 13,
                fontFamily: 'DM Sans, sans-serif',
                boxSizing: 'border-box'
              }}
            />
          </Box>
          <Box sx={{ marginBottom: 14 }}>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: 'var(--gs-muted)',
                marginBottom: 6
              }}
            >
              Brief Description (optional)
            </AppText>
            <textarea
              placeholder="What is this model known for? Key features, variants, reputation..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                background: 'var(--gs-surface2)',
                border: '1.5px solid var(--gs-border-bright)',
                color: 'var(--gs-text)',
                padding: '10px 14px',
                borderRadius: 5,
                fontSize: 13,
                fontFamily: 'DM Sans, sans-serif',
                boxSizing: 'border-box',
                resize: 'vertical'
              }}
            />
          </Box>
        </>
      )}

      {/* Step 5 — Review */}
      {step === 5 && (
        <>
          <AppText
            component="h2"
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 500,
              fontSize: 16,
              textTransform: 'uppercase',
              color: 'var(--gs-text)',
              marginBottom: 16
            }}
          >
            LOCKED AND LOADED — REVIEW YOUR SUBMISSION
          </AppText>
          <Box
            sx={{
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              borderRadius: 8,
              padding: 20
            }}
          >
            {[
              { label: 'Platform', value: platform ?? '—', goTo: 1 },
              { label: 'Make', value: resolvedMake ?? '—', goTo: 2 },
              { label: 'Caliber', value: resolvedCaliber ?? '—', goTo: 3 },
              { label: 'Model Name', value: modelName.trim() || '—', goTo: 4 },
              { label: 'Year', value: yearIntroduced.trim() || '—', goTo: 4 },
              {
                label: 'Description',
                value: description.trim() ? (description.trim().length > 80 ? description.trim().slice(0, 80) + '…' : description.trim()) : '—',
                goTo: 4
              }
            ].map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom: '1px solid var(--gs-border)',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)', flexShrink: 0 }}>
                  {row.label}
                </AppText>
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--gs-text)', flex: 1, textAlign: 'right' }}>
                  {row.value}
                </AppText>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setStep(row.goTo)}
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 11,
                    color: '#E85D04',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0
                  }}
                >
                  Change
                </Box>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              background: 'rgba(210,153,34,0.1)',
              border: '1px solid rgba(210,153,34,0.3)',
              borderRadius: 6,
              padding: 12,
              fontSize: 12,
              color: '#D29922',
              marginTop: 16,
              fontFamily: 'DM Sans, sans-serif'
            }}
          >
            Your submission enters the moderator review queue. You'll be notified when it's approved.
          </Box>
          {submitError && (
            <AppText sx={{ color: '#f85149', fontSize: 13, marginTop: 12 }}>{submitError}</AppText>
          )}
          <AppButton
            fullWidth
            onClick={handleSubmit}
            loading={loading}
            sx={{
              marginTop: 20,
              background: '#E85D04',
              '&:hover': { background: '#FF6A1A' }
            }}
          >
            Submit for Review
          </AppButton>
        </>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 28, gap: 12 }}>
        <Box sx={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {step > 1 && (
            <AppButton
              variant="outline"
              onClick={handleBack}
              sx={{
                background: '#21262D',
                border: '1.5px solid #484F58',
                color: '#E6EDF3',
                '&:hover': { background: '#30363D', color: '#E6EDF3' }
              }}
            >
              Back
            </AppButton>
          )}
          {step < TOTAL_STEPS && (
            <AppButton
              onClick={handleNext}
              disabled={!canProceed}
              sx={{
                background: '#E85D04',
                '&:hover': { background: '#FF6A1A' }
              }}
            >
              Next
            </AppButton>
          )}
        </Box>
        <Link
          to="/registry"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            color: 'var(--gs-muted)'
          }}
        >
          Cancel
        </Link>
      </Box>
    </Box>
  )
})
